import { useRouter } from 'next/router'
import cn from 'classnames'
import React, { type ReactElement, useEffect } from 'react'
import Head from 'next/head'
import 'react-toastify/dist/ReactToastify.css'
import { useSnapshot } from 'valtio'
import Breadcrumbs from '../../../../shared/components/ui/breadcrumbs/Breadcrumbs'
import { APP_RECURRING_PAYMENTS_PATH } from '../../../../pages/app/recurring-charges'
import { PageHeading } from '../../../../shared/components/ui/headings/PageHeading'
import { proxyGlobalAuth } from '../../../../shared/proxies/proxyGlobalAuth'
import { determineBrowserTabName } from '../../../../shared/themes/logoFunctions'
import EndRecurringPaymentButton from '../../../../shared/components/ui/buttons/EndRecurringPaymentButton'
import { recurringPaymentProxy } from '../../../../shared/proxies/recurringPaymentProxy'
import {
  PRETTY_RECURRING_PAYMENT_STATUS,
  RecurringPaymentStatus
} from '../../../../shared/types/GlobalRecurringPaymentsStatus'
import XStopIcon from '../../../../shared/components/ui/icons/XStopIcon'
import {
  determineStatusColorClass
} from '../index/components/RecurringPaymentsTable/components/RecurringPaymentsRows/components/AmountCell/utils'
import { CardAddSlideOver } from '../../../../shared/components/CardAddSlideOver/CardAddSlideOver'
import {
  handleSubmitToOpenLightboxEditRecurringConfiguration
} from '../../../../shared/components/CardAddSlideOver/utils'
import { executeRecurringTransactionRetrieve } from './executeRecurringTransactionRetrieve'
import { ConfirmPauseModal } from './modals/ConfirmPauseModal'
import { ConfirmEndModal } from './modals/ConfirmEndModal'
import { RpOccurrencesTable } from './RpOccurrencesTable/RpOccurrencesTable'
import { RecurringPaymentDetailMainContainer } from './RecurringPaymentDetail/RecurringPaymentDetailMainContainer'
import { CustomerContainer } from './RecurringPaymentDetail/components/CustomerContainer'
import PauseButton from './RecurringPaymentDetail/components/PauseButton'
import { ConfirmResumeModal } from './modals/ConfirmResumeModal'
import { RecurringConfigurationEditSlideOver } from './modals/EditSlideOver/RecurringConfigurationEditSlideOver'
import ResumeButton from './RecurringPaymentDetail/components/ResumeButton'

export function RecurringPaymentsDetailContainer (): ReactElement {
  const router = useRouter()
  const { id } = router.query
  const { siteTheme: { tableMainColors } } = useSnapshot(proxyGlobalAuth)
  const { recurringPayment } = useSnapshot(recurringPaymentProxy)

  useEffect(() => {
    if (!id) { return }
    void executeRecurringTransactionRetrieve(id as string)
  }, [id])

  if (!recurringPayment) {
    return (<></>)
  }

  const isEnded = recurringPayment?.status === RecurringPaymentStatus.Ended
  const isPausedOrErrored = recurringPayment?.status === RecurringPaymentStatus.Paused || recurringPayment?.status === RecurringPaymentStatus.Errored

  return (
    <>
      <Head>
        <title>{`Recurring Charge ${id as string} - ${determineBrowserTabName()}`}</title>
      </Head>

      <ConfirmPauseModal />
      <ConfirmEndModal />
      <ConfirmResumeModal />

      <RecurringConfigurationEditSlideOver />
      <CardAddSlideOver submitFunction={handleSubmitToOpenLightboxEditRecurringConfiguration} />
      <Breadcrumbs breadcrumbs={[{ path: APP_RECURRING_PAYMENTS_PATH, text: 'Recurring Charges' }, { text: (id as string) }]} />
      <PageHeading text="View and suspend ongoing payments" />
      <div className="flex ">

        <div className="flex flex-row w-full justify-end mb-4">
          {!isEnded &&
            <EndRecurringPaymentButton
              clickFunction={() => { recurringPaymentProxy.displayEndModal = true }}
              icon={<XStopIcon color="#EF4444" />} text="End"
            />}
          {isPausedOrErrored &&
            <ResumeButton isEnded={isEnded} />}
          {recurringPayment?.status === RecurringPaymentStatus.Active &&
            <PauseButton isEnded={isEnded} />}

          <div className={cn('border rounded-full flex items-center justify-center py-1 px-2', determineStatusColorClass(recurringPayment))}> {PRETTY_RECURRING_PAYMENT_STATUS[recurringPayment.status]}</div>
        </div>
      </div>

      <div className="flex ">

        <div className="flex flex-row w-full">
          <div className="w-2/3">
            <RecurringPaymentDetailMainContainer />
          </div>
          <div className="w-1/3">
            <CustomerContainer />
          </div>
        </div>
      </div>
      <div className={cn(tableMainColors.classes, 'bg-white shadow rounded mb-32')}>
        <RpOccurrencesTable />
      </div>

    </>
  )
}
